import {
  Icon,
  Avatar,
  Modal,
  NoDataPage,
  CircularLoader,
} from "@components/index";
import { formatDate, formatCurrency } from "@utils/index";
import { URL_CONFIG } from "@config/urlConfig";
import DeleteClient from "./components/DeleteClient";
import { useClientListRenderData } from "./clientListRenderData";
import "./index.scss";

const ClientListRender = ({
  clientList,
  loading,
  error,
  onClearFilters,
  onEditClient,
}) => {
  const {
    onDeleteClient,
    showDeleteClientDrawer,
    setShowDeleteClientDrawer,
    deleteClientDetails,
    confirmDeleteClient,
  } = useClientListRenderData(onClearFilters);

  // Helper function to get default values for null fields
  const getDisplayValue = (value, defaultValue, unit = "") => {
    if (value === null || value === undefined) {
      return `${defaultValue}${unit}`;
    }
    return `${value}${unit}`;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="client-list-render__loading">
        <CircularLoader
          message="Loading Clients Data..."
          className="client-list-render__loading-container"
        />
      </div>
    );
  }

  // Show client list if we have data
  if (clientList && clientList.length > 0 && !error) {
    return (
      <>
        <div className="client-list-container">
          {clientList.map((client) => {
            const isDeleted = client.client_status?.toLowerCase() === "deleted";

            return (
              <div
                className={`client-card ${isDeleted ? "client-card--deleted" : ""}`}
                key={client.client_id}
              >
                {/* Card Header with Status and Actions */}
                <div className="client-card-header">
                  <span
                    className={`client-status client-status--${client.client_status?.toLowerCase()}`}
                  >
                    {client.client_status}
                  </span>

                  {!isDeleted && (
                    <div className="client-actions">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => onEditClient(client)}
                        title="Edit Client"
                      >
                        <Icon iconName="edit" width={16} height={16} />
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => onDeleteClient(client)}
                        title="Delete Client"
                      >
                        <Icon iconName="delete" width={16} height={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Main Content */}
                <div className="client-info">
                  <Avatar nameInitials={client.client_name} />

                  <div className="client-details">
                    <div className="client-header">
                      <h3 className="client-name">{client.client_name}</h3>
                    </div>
                    <p className="client-id">ID: {client.client_id}</p>

                    <div className="client-balance-info">
                      <div className="balance-item">
                        <span className="balance-label">
                          Opening CD Balance:
                        </span>
                        <span className="balance-value">
                          {formatCurrency(client.opening_cd_balance)}
                        </span>
                      </div>
                      <div className="balance-item">
                        <span className="balance-label">
                          Current CD Balance:
                        </span>
                        <span className="balance-value">
                          {formatCurrency(client.current_cd_balance)}
                        </span>
                      </div>
                    </div>

                    {/* New Voucher Configuration Section */}
                    <div className="client-voucher-config">
                      <h4 className="config-title">Voucher Configuration</h4>
                      <div className="config-grid">
                        <div className="config-item">
                          <span className="config-label">
                            Claim Intimation:
                          </span>
                          <span className="config-value">
                            {getDisplayValue(
                              client.claim_intimation_timeline,
                              URL_CONFIG.VOUCHER_DEFAULT_VALUES
                                .CLAIM_INTIMATION,
                              " hrs",
                            )}
                          </span>
                        </div>
                        <div className="config-item">
                          <span className="config-label">
                            Max Voucher Value:
                          </span>
                          <span className="config-value">
                            {getDisplayValue(
                              client.maximum_voucher_value,
                              URL_CONFIG.VOUCHER_DEFAULT_VALUES
                                .MAXIMUM_VOUCHER_VALUE,
                              " INR",
                            )}
                          </span>
                        </div>
                        <div className="config-item">
                          <span className="config-label">
                            Voucher Validity:
                          </span>
                          <span className="config-value">
                            {getDisplayValue(
                              client.voucher_validity_timeline,
                              URL_CONFIG.VOUCHER_DEFAULT_VALUES
                                .VOUCHER_VALIDITY,
                              " days",
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="client-meta">
                      <div className="meta-row">
                        <p className="client-created-by">
                          Created by: {client.created_by?.name} (
                          {client.created_by?.role})
                        </p>
                        <p className="client-created-at">
                          Created: {formatDate(client.created_at)}
                        </p>
                      </div>

                      {client.last_transaction_at && (
                        <p className="client-last-transaction">
                          Last Transaction:{" "}
                          {formatDate(client.last_transaction_at)}
                        </p>
                      )}

                      <p className="client-transactions">
                        Total Transactions: {client.total_transactions || 0}
                      </p>

                      {/* Deleted Client Information */}
                      {isDeleted && client.deleted_by && (
                        <div className="deleted-info">
                          <p className="deleted-by">
                            Deleted by: {client.deleted_by.name} (
                            {client.deleted_by.role})
                          </p>
                          <p className="deleted-at">
                            Deleted: {formatDate(client.updated_at)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showDeleteClientDrawer && (
          <Modal
            isOpen={showDeleteClientDrawer}
            onClose={() => setShowDeleteClientDrawer(false)}
            className="my-custom-modal"
            closeOnOutsideClick={true}
          >
            <DeleteClient
              deleteClientDetails={deleteClientDetails}
              handleDeleteCancel={() => setShowDeleteClientDrawer(false)}
              handleDeleteConfirm={confirmDeleteClient}
            />
          </Modal>
        )}
      </>
    );
  }

  // Show no data state
  return (
    <div className="client-list-render__no-data">
      <NoDataPage
        showRetryButton
        onButtonClick={onClearFilters}
        buttonText={error ? "Retry" : "Reset Filters"}
        message={error ? "Failed to load clients data" : "No clients found"}
      />
    </div>
  );
};

export default ClientListRender;
